import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, PlayCircle } from "lucide-react";
import { Link } from "react-router-dom";
import NexWordmark from "@/components/NexWordmark";
import HeroVideoCard from "@/components/HeroVideoCard";
import { IMAGES } from "@/lib/assets";
import { useSmartScroll } from "@/lib/useSmartScroll";

const stagger = { animate: { transition: { staggerChildren: 0.09 } } };
const item = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.19, 1, 0.22, 1] } },
};

const Hero = () => {
  const smart = useSmartScroll();

  return (
    <section id="top" data-testid="hero-section" className="relative pt-32 pb-0 overflow-hidden">
      {/* background layers */}
      <div className="absolute inset-0 -z-10">
        <img
          src={IMAGES.heroBg}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F19] via-[#0B0F19]/85 to-[#0B0F19]" />
        <div className="absolute inset-0 blueprint-bg opacity-60" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[900px] bg-radial-gold opacity-70 blur-3xl" />
      </div>

      {/* Editorial chrome — floating coordinates in the side margins (visible at xl+) */}
      <div className="hidden xl:block absolute top-40 left-6 mono-body text-[10px] text-white/30 tracking-widest -rotate-90 origin-top-left translate-y-4">
        LAT 40.7128° · LON −74.0060° · v3.2
      </div>
      <div className="hidden xl:block absolute top-40 right-6 mono-body text-[10px] text-white/30 tracking-widest rotate-90 origin-top-right -translate-y-4">
        NEX_CORE // BUILD 2026.02 // LIVE
      </div>

      {/* Top strip — A LIFE LLC INITIATIVE (full-width) */}
      <motion.div
        variants={item}
        initial="initial"
        animate="animate"
        className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 flex items-center justify-center gap-4 mb-10"
      >
        <div className="h-px w-16 bg-nex-gold" />
        <span className="mono-label text-nex-gold">A LIFE LLC Initiative // Est. 2023</span>
        <div className="h-px w-16 bg-nex-gold" />
      </motion.div>

      {/* Full-width headline banner */}
      <motion.h1
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.19, 1, 0.22, 1] } }}
        className="serif-head hero-glow text-center w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 mb-16 text-[clamp(1.75rem,5.5vw,5rem)] py-2 leading-[1.05] [text-wrap:balance]"
        style={{ lineHeight: 1.15 }}
        data-testid="hero-headline"
      >
        <span className="text-nex-silver">Custom</span>{" "}
        <span className="italic text-nex-gold">Business Engines.</span>{" "}
        <span className="chrome-text">Deployed in</span>{" "}
        <span className="font-black text-nex-silver" style={{ fontWeight: 900, letterSpacing: "-0.03em" }}>14 days.</span>
      </motion.h1>

      {/* 3-column card layout: description | video | status */}
      <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 grid lg:grid-cols-3 gap-8 lg:gap-12 items-start pb-20">
        {/* Left column — description, CTAs, stats */}
        <motion.div
          variants={stagger}
          initial="initial"
          animate="animate"
        >
          <motion.p variants={item} className="text-lg text-nex-chrome leading-relaxed mb-10">
            We are not web developers. We are <span className="text-nex-silver font-semibold">Operational Architects</span>. NEX is a proprietary software framework that replaces paper, spreadsheets, and duct-taped SaaS with a single custom engine — tuned to your industry, deployed on your terms, in a fraction of the time.
          </motion.p>

          <motion.div variants={item} className="flex flex-wrap items-center gap-4 mb-12">
            <button
              onClick={() => smart("#friction-audit")}
              data-testid="hero-cta-primary"
              className="tick-btn-primary text-base"
            >
              Start Friction Audit <ArrowRight size={18} />
            </button>
            <Link
              to="/demos"
              data-testid="hero-cta-live-demo"
              className="tick-btn-secondary"
            >
              <PlayCircle size={16} /> Live Demo
            </Link>
          </motion.div>

          <motion.div variants={item} className="grid grid-cols-3 gap-4 border-t border-white/10 pt-8">
            <div>
              <div className="mono-label mb-2">Deploy Speed</div>
              <div className="serif-head text-2xl md:text-3xl">14 <span className="text-nex-chrome text-lg">days</span></div>
              <div className="mono-body mt-1 text-[11px]">vs. 4 months traditional</div>
            </div>
            <div>
              <div className="mono-label mb-2">Ops Waste Recovered</div>
              <div className="serif-head text-2xl md:text-3xl gold-text">6-Figures</div>
              <div className="mono-body mt-1 text-[11px]">avg · per full ecosystem deploy</div>
            </div>
            <div>
              <div className="mono-label mb-2">Custom Build</div>
              <div className="serif-head text-2xl md:text-3xl">100<span className="text-nex-chrome text-lg">%</span></div>
              <div className="mono-body mt-1 text-[11px]">bespoke · your workflow · your stack</div>
            </div>
          </motion.div>
        </motion.div>

        {/* Middle column — 30-second intro commercial */}
        <motion.div
          variants={item}
          initial="initial"
          animate="animate"
          className="hidden lg:block"
        >
          <HeroVideoCard />
        </motion.div>

        {/* Right column — technical readout */}
        <motion.div
          variants={item}
          initial="initial"
          animate="animate"
          className="hidden lg:block"
          data-testid="hero-status-rail"
        >
          <div data-testid="hero-status-card" className="card-industrial corner-tick p-6 relative overflow-hidden scanline">
            <div className="flex items-center justify-between mb-5">
              <span className="mono-label">// NEX_CORE_STATUS</span>
              <span className="flex items-center gap-2 mono-body text-[11px]">
                <span className="w-1.5 h-1.5 rounded-full bg-nex-gold animate-pulse-gold" />
                ONLINE
              </span>
            </div>

            <NexWordmark size="text-6xl" className="block mb-6" />

            <div className="space-y-3 mono-body text-[11px]">
              {[
                ["module", "Flow.Engine v3.2"],
                ["module", "Booking.Engine v2.8"],
                ["module", "Capital.Engine v2.1"],
                ["parent", "LIFE LLC (EIN Verified)"],
                ["founded", "01.10.2023"],
                ["arch", "Modular Scalability"],
              ].map(([k, v]) => (
                <div key={`${k}-${v}`} className="flex justify-between border-b border-white/5 pb-2">
                  <span className="text-nex-muted uppercase tracking-widest">{k}</span>
                  <span className="text-nex-silver">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Full-width contrast band — Nexus Enterprise X-Engine attribution */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.8, delay: 0.4 } }}
        data-testid="hero-attribution-band"
        className="relative w-full bg-[#0f1524] border-y border-nex-gold/40 py-6"
      >
        <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32">
          <p className="mono-body text-sm md:text-base text-nex-chrome text-center leading-relaxed">
            &gt; <span className="text-nex-gold">Nexus Enterprise X-Engine</span> is the digital infrastructure arm of <span className="text-nex-silver">Learning Institute For Empowerment LLC</span>.
          </p>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
